import { AccountData, DayOccupancyData, OrderData, SchedulerConfigurationData } from '../../../api';
import { getOrderPages } from '../../../utils/AccountUtils';
import { localizedLabel, maskToIndexes, resourceDisplayName } from '../../../utils/maskUtils';
import { seatedConfigIds, seatedIndexesInConfig } from '../../utils/orderSeating';
import { accountSchedulerConfigs } from './occupancyMath';
import { areaUsesTicketWording } from './workingHours';

export enum OrderPlacementType {
  AREAS = 'areas',
  UNASSIGNED = 'unassigned',
  OFF_GRID = 'off_grid',
  NON_SCHEDULER = 'non_scheduler',
}

export type OrderAreaInfo = {
  configId: string;
  areaId: string;
  label: string;
  tablesInArea: number;
  tableLabels: string[];
  usesTickets: boolean;
};

export type OrderPlacement =
  | { type: OrderPlacementType.AREAS; areas: OrderAreaInfo[] }
  | { type: OrderPlacementType.UNASSIGNED; configId: string }
  | { type: OrderPlacementType.OFF_GRID; configId: string }
  | { type: OrderPlacementType.NON_SCHEDULER; pageLabel: string };

export const orderPlacement = (
  order: OrderData,
  account: AccountData,
  occupancy: DayOccupancyData | undefined,
  language: string,
): OrderPlacement => {
  const accountConfigs = accountSchedulerConfigs(account);
  const configs = seatedConfigIds(order, account)
    .map((configId) => accountConfigs.find((candidate) => candidate.id === configId))
    .filter((candidate): candidate is SchedulerConfigurationData => candidate !== undefined);

  if (configs.length === 0) {
    const page = getOrderPages(account, true).find((candidate) => candidate.id === order.order_page_id);
    return {
      type: OrderPlacementType.NON_SCHEDULER,
      pageLabel: page?.label?.[language as 'he' | 'en'] || page?.label?.he || '',
    };
  }
  if (order.skip_resource_assignment) {
    return { type: OrderPlacementType.OFF_GRID, configId: configs[0].id };
  }

  const areas = configs.flatMap((config) => {
    const orderIndexes = seatedIndexesInConfig(order, config);
    return (occupancy?.scheduler_configs.find((candidate) => candidate.scheduler_config_id === config.id)?.areas ?? [])
      .map((area) => {
        const areaIndexes = maskToIndexes(area.mask);
        const indexesInArea = areaIndexes.filter((index) => orderIndexes.includes(index));
        return { area, indexesInArea };
      })
      .filter(({ indexesInArea }) => indexesInArea.length > 0)
      .map(
        ({ area, indexesInArea }): OrderAreaInfo => ({
          configId: config.id,
          areaId: area.area_id,
          label: localizedLabel(area.label, language, area.area_id),
          tablesInArea: indexesInArea.length,
          tableLabels: indexesInArea.map((index) => resourceDisplayName(index, config, language)),
          usesTickets: areaUsesTicketWording(area, config),
        }),
      );
  });

  return areas.length > 0
    ? { type: OrderPlacementType.AREAS, areas }
    : { type: OrderPlacementType.UNASSIGNED, configId: configs[0].id };
};
